import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Alert,
} from "@mui/material";
import BrandSpinner from "./BrandSpinner";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import { SECONDARY_BUTTON_SX } from "../../theme/buttonstyles";
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";
import NotesIcon from "@mui/icons-material/Notes";
import CallIcon from "@mui/icons-material/Call";
import GroupsIcon from "@mui/icons-material/Groups";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import TimelineIcon from "@mui/icons-material/Timeline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import { useAuth } from "../../providers/AuthProvider";
import api, { isCancel } from "../../services/api/axios";
import MessageViewer from "../../features/outlook/MessageViewer";
import {
  BRAND_MODAL_PAPER_PROPS,
  BRAND_MODAL_TITLE_SX,
} from "./modalStyles";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PDF_MIME_TYPE = "application/pdf";
const MAX_PDF_ATTACHMENT_BYTES = 10 * 1024 * 1024;


const TYPE_CONFIG = {
  email:   { color: "#1565C0", bg: "#E3F2FD", label: "Email",   Icon: EmailIcon },
  note:    { color: "#92400E", bg: "#FEF3C7", label: "Note",    Icon: NotesIcon },
  call:    { color: "#166534", bg: "#DCFCE7", label: "Call",    Icon: CallIcon },
  meeting: { color: "#5B21B6", bg: "#EDE9FE", label: "Meeting", Icon: GroupsIcon },
  task:    { color: "#0E7490", bg: "#CFFAFE", label: "Task",    Icon: TaskAltIcon },
  status:  { color: "#374151", bg: "#F3F4F6", label: "Status",  Icon: TimelineIcon },
  file:    { color: "#4338CA", bg: "#E0E7FF", label: "File",    Icon: AttachFileIcon },
};

const TYPE_FILTERS = [
  { value: "", label: "All" },
  { value: "email",   label: "Email" },
  { value: "note",    label: "Note" },
  { value: "call",    label: "Call" },
  { value: "meeting", label: "Meeting" },
  { value: "task",    label: "Task" },
  { value: "status",  label: "Status" },
  { value: "file",    label: "File" },
];

function sortNewestFirst(items) {
  return [...items].sort((a, b) =>
    (b?.occurred_at ? new Date(b.occurred_at).getTime() : 0) -
    (a?.occurred_at ? new Date(a.occurred_at).getTime() : 0)
  );
}

function formatRelativeDate(dateStr) {
  if (!dateStr) return "";
  const d = dayjs(dateStr);
  const now = dayjs();
  const diffMins = now.diff(d, "minute");
  const diffHours = now.diff(d, "hour");
  const diffDays = now.diff(d, "day");
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 1) return "Yesterday";
  if (diffDays < 7) return d.format("ddd, MMM D");
  return d.format("MMM D, YYYY");
}

function normalizeListResponse(data) {
  if (Array.isArray(data)) return { list: data, total: data.length };
  if (data && Array.isArray(data.items)) {
    return { list: data.items, total: Number.isFinite(data.total) ? data.total : data.items.length };
  }
  return { list: [], total: 0 };
}

function normalizeTaggedEmailItem(email) {
  return {
    id: `company-email-${email.id}`,
    raw_id: email.id,
    type: "email",
    subject: email.subject || "(no subject)",
    body_text: email.body_preview || "",
    occurred_at: email.received_at || email.created_at,
    recipients: Array.isArray(email.recipients) ? email.recipients.map((r) => ({ email: r })) : [],
    internet_message_id: email.outlook_message_id,
    graph_message_id: email.outlook_message_id,
    read_only: true,
    source_kind: "company_email",
    sender: email.sender || "",
  };
}

function formatErrorDetail(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        const location = Array.isArray(item.loc) ? item.loc.join(".") : item.loc || "field";
        return `${location}: ${item.msg || JSON.stringify(item)}`;
      }
      return String(item);
    }).join(" | ");
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
}

function isPdfFile(file) {
  return file?.type === PDF_MIME_TYPE || String(file?.name || "").toLowerCase().endsWith(".pdf");
}

function formatFileSize(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

async function buildPdfAttachment(file) {
  if (!isPdfFile(file)) {
    throw new Error("Activity file must be a PDF.");
  }
  if (file.size > MAX_PDF_ATTACHMENT_BYTES) {
    throw new Error("PDF must be 10 MB or smaller.");
  }
  return {
    name: file.name || "activity-attachment.pdf",
    size: file.size,
    contentType: PDF_MIME_TYPE,
    file,
    previewUrl: window.URL.createObjectURL(file),
    uploaded_at: new Date().toISOString(),
  };
}

function getAttachmentUrl(attachment) {
  return attachment?.previewUrl || attachment?.url || attachment?.contentUrl || attachment?.dataUrl || "";
}

function getAttachmentName(attachment) {
  return attachment?.name || attachment?.original_name || "activity-attachment.pdf";
}

function openLocalAttachment(attachment) {
  const attachmentUrl = getAttachmentUrl(attachment);
  if (!attachmentUrl) return;
  window.open(attachmentUrl, "_blank", "noopener,noreferrer");
}

function downloadLocalAttachment(attachment) {
  const attachmentUrl = getAttachmentUrl(attachment);
  if (!attachmentUrl) return;
  const link = document.createElement("a");
  link.href = attachmentUrl;
  link.download = getAttachmentName(attachment);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function fetchAttachmentBlob({ activityId, attachmentIndex, apiBase }) {
  const path = `/company-activity/${activityId}/attachments/${attachmentIndex}`;
  const response = await api.get(apiBase ? `${apiBase}${path}` : path, {
    responseType: "blob",
  });
  return response.data;
}

async function openSavedAttachment({ activityId, attachmentIndex, attachment, apiBase }) {
  if (!activityId) {
    openLocalAttachment(attachment);
    return;
  }
  const blob = await fetchAttachmentBlob({ activityId, attachmentIndex, apiBase });
  const fileUrl = window.URL.createObjectURL(new Blob([blob], { type: PDF_MIME_TYPE }));
  window.open(fileUrl, "_blank", "noopener,noreferrer");
  window.setTimeout(() => window.URL.revokeObjectURL(fileUrl), 60_000);
}

async function downloadSavedAttachment({ activityId, attachmentIndex, attachment, apiBase }) {
  if (!activityId) {
    downloadLocalAttachment(attachment);
    return;
  }
  const blob = await fetchAttachmentBlob({ activityId, attachmentIndex, apiBase });
  const fileUrl = window.URL.createObjectURL(new Blob([blob], { type: PDF_MIME_TYPE }));
  const link = document.createElement("a");
  link.href = fileUrl;
  link.download = getAttachmentName(attachment);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(fileUrl);
}

function ActivityAttachmentField({ attachments = [], onAdd, onRemove, inputRef, disabled = false }) {
  return (
    <Box>
      <input
        ref={inputRef}
        hidden
        type="file"
        accept="application/pdf,.pdf"
        onChange={onAdd}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<AttachFileIcon />}
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        sx={{ alignSelf: "flex-start", color: BRAND_BLUE, borderColor: "rgba(0,51,108,0.35)" }}
      >
        Add PDF
      </Button>
      {attachments.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {attachments.map((attachment, index) => (
            <Box
              key={`${getAttachmentName(attachment)}-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                border: "1px solid rgba(0,51,108,0.12)",
                borderRadius: 1,
                px: 1.25,
                py: 0.75,
                bgcolor: "rgba(0,51,108,0.03)",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: BRAND_BLUE, fontWeight: 600 }} noWrap>
                  {getAttachmentName(attachment)}
                </Typography>
                {attachment.size ? (
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(attachment.size)}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                <Tooltip title="Open PDF">
                  <IconButton size="small" onClick={() => openLocalAttachment(attachment)}>
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download PDF">
                  <IconButton size="small" onClick={() => downloadLocalAttachment(attachment)}>
                    <DownloadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => onRemove(index)} sx={{ "&:hover": { color: "#D32F2F" } }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default function CompanyActivityTracker({ companyId, prospectId, projectId, apiBase, pageSize = 25 }) {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [serverTotal, setServerTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    company_id: companyId, contact_id: null, type: "note",
    subject: "", body_text: "", body_html: "", recipients: [], attachments: [],
    occurred_at: "",
  });
  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    subject: "", body_text: "", body_html: "", type: "note",
    occurred_at: null, contact_id: null, recipients: [], attachments: [],
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerMessageId, setViewerMessageId] = useState(null);
  const [viewerAutoLog, setViewerAutoLog] = useState(true);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [warning, setWarning] = useState("");
  const didRetryRef = useRef(false);
  const listAbortRef = useRef(null);
  const createFileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const url = (path) => (apiBase ? `${apiBase}${path}` : path);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 400);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setForm((f) => ({ ...f, company_id: companyId ?? null, prospect_id: prospectId ?? null, project_id: projectId ?? null }));
  }, [companyId, prospectId, projectId]);

  useEffect(() => { setPage(1); }, [companyId, prospectId, projectId]);

  async function fetchList({ silent = false } = {}) {
    if ((!companyId && !prospectId && !projectId) || !token) return;
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    if (!silent) setLoading(true);
    setWarning("");

    async function fetchWithEmails(activityPath, emailPath) {
      const [activityResult, emailResult] = await Promise.allSettled([
        api.get(url(`${activityPath}?page=1&limit=100`), { signal: controller.signal }),
        api.get(url(emailPath), { signal: controller.signal }),
      ]);
      if (activityResult.status === "rejected") throw activityResult.reason;
      const { list: activityItems } = normalizeListResponse(activityResult.value.data);
      const emailItems =
        emailResult.status === "fulfilled" && Array.isArray(emailResult.value.data)
          ? emailResult.value.data.map(normalizeTaggedEmailItem)
          : [];
      return [...activityItems, ...emailItems];
    }

    try {
      let merged;
      if (companyId && !prospectId && !projectId) {
        merged = await fetchWithEmails(`/company-activity/company/${companyId}`, `/companies/${companyId}/emails`);
      } else if (prospectId && !projectId) {
        merged = await fetchWithEmails(`/company-activity/prospect/${prospectId}`, `/prospecting-companies/${prospectId}/emails`);
      } else if (projectId) {
        merged = await fetchWithEmails(`/company-activity/project/${projectId}`, `/projects/${projectId}/emails`);
      } else {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(pageSize));
        if (typeFilter) params.set("types", typeFilter);
        if (debouncedQ) params.set("q", debouncedQ);
        const scopePath = projectId
          ? `/company-activity/project/${projectId}`
          : prospectId
            ? `/company-activity/prospect/${prospectId}`
            : `/company-activity/company/${companyId}`;
        const res = await api.get(url(`${scopePath}?${params.toString()}`), { signal: controller.signal });
        const { list, total } = normalizeListResponse(res.data);
        setItems(list);
        setServerTotal(total);
        didRetryRef.current = false;
        return;
      }

      if (typeFilter) merged = merged.filter((item) => item.type === typeFilter);
      if (debouncedQ) {
        const query = debouncedQ.trim().toLowerCase();
        merged = merged.filter((item) =>
          [item.subject, item.body_text, item.body_html, item.sender].filter(Boolean).some((v) => String(v).toLowerCase().includes(query))
        );
      }
      merged = sortNewestFirst(merged);
      const total = merged.length;
      const start = (page - 1) * pageSize;
      setItems(merged.slice(start, start + pageSize));
      setServerTotal(total);
      didRetryRef.current = false;
    } catch (err) {
      if (isCancel(err)) return;
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 401) {
        if (!didRetryRef.current) { didRetryRef.current = true; setTimeout(() => fetchList({ silent: true }), 300); }
        else { setWarning("Unauthorized. Please re-authenticate."); if (!silent) { setItems([]); setServerTotal(0); } }
      } else {
        setWarning(formatErrorDetail(data?.detail ?? data, "Failed to load activity."));
        if (!silent) { setItems([]); setServerTotal(0); }
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    if ((!companyId && !prospectId && !projectId) || !token) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, prospectId, projectId, page, pageSize, typeFilter, debouncedQ, token, apiBase]);

  function buildCreatePayload() {
    return {
      company_id: companyId ?? undefined, prospect_id: prospectId ?? undefined, project_id: projectId ?? undefined,
      contact_id: form.contact_id ?? undefined, type: form.type || "note", subject: form.subject.trim(),
      body_text: form.body_text?.trim() || undefined, body_html: form.body_html?.trim() || undefined,
      recipients: Array.isArray(form.recipients) ? form.recipients : [],
      attachments: Array.isArray(form.attachments) ? form.attachments : [],
      occurred_at: form.occurred_at ? dayjs(form.occurred_at).toISOString() : undefined,
    };
  }

  function buildFileActivityFormData(sourceForm) {
    const data = new FormData();
    if (companyId) data.append("company_id", String(companyId));
    if (prospectId) data.append("prospect_id", String(prospectId));
    if (projectId) data.append("project_id", String(projectId));
    if (sourceForm.contact_id) data.append("contact_id", String(sourceForm.contact_id));
    if (sourceForm.subject?.trim()) data.append("subject", sourceForm.subject.trim());
    if (sourceForm.occurred_at) data.append("occurred_at", dayjs(sourceForm.occurred_at).toISOString());
    const file = (sourceForm.attachments || []).find((attachment) => attachment.file)?.file;
    if (file) data.append("file", file, file.name);
    return data;
  }

  function updateCreateType(nextType) {
    setForm((prev) => ({
      ...prev,
      type: nextType,
      attachments: nextType === "file" ? prev.attachments : [],
    }));
  }

  function updateEditType(nextType) {
    setEditForm((prev) => ({
      ...prev,
      type: nextType,
      attachments: nextType === "file" ? prev.attachments : [],
    }));
  }

  const onCreate = async () => {
    if (!token) return;
    if (form.type === "file" && !(form.attachments || []).length) { setWarning("Add a PDF before saving a file activity."); return; }
    if (!form.subject || !form.subject.trim()) { setWarning("Subject is required."); return; }
    setCreating(true);
    try {
      const res = form.type === "file"
        ? await api.post(url("/company-activity/file"), buildFileActivityFormData(form))
        : await api.post(url("/company-activity/"), { ...buildCreatePayload(), subject: form.subject.trim() });
      const created = Array.isArray(res.data) ? res.data[0] : res.data;
      setItems((prev) => sortNewestFirst([created, ...prev]));
      setOpenCreate(false);
      setForm((f) => ({ ...f, type: "note", subject: "", body_text: "", body_html: "", recipients: [], attachments: [], occurred_at: "" }));
    } catch (err) {
      setWarning(formatErrorDetail(err?.response?.data?.detail, "Failed to save activity."));
    } finally {
      setCreating(false);
    }
  };

  async function addCreateAttachment(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const attachment = await buildPdfAttachment(file);
      setForm((prev) => ({
        ...prev,
        subject: prev.subject?.trim() ? prev.subject : getAttachmentName(attachment),
        attachments: [attachment],
      }));
      setWarning("");
    } catch (err) {
      setWarning(err?.message || "Failed to add PDF.");
    }
  }

  function removeCreateAttachment(index) {
    setForm((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, currentIndex) => currentIndex !== index),
    }));
  }

  function openEmail(item) {
    const graphId = item.graph_message_id || item.external_id || item.internet_message_id;
    if (!graphId) return;
    setViewerMessageId(graphId);
    setViewerAutoLog(item.source_kind !== "company_email");
    setViewerOpen(true);
  }

  async function onViewerLogged() { await fetchList({ silent: true }); }

  function openEdit(item) {
    setEditingItem(item);
    setEditForm({
      subject: item.subject || "", body_text: item.body_text || "", body_html: item.body_html || "",
      type: item.type || "note", occurred_at: item.occurred_at ? dayjs(item.occurred_at).toDate() : null,
      contact_id: item.contact_id || null,
      recipients: Array.isArray(item.recipients) ? item.recipients : [],
      attachments: Array.isArray(item.attachments) ? item.attachments : [],
    });
    setEditOpen(true);
  }

  function buildEditPayload() {
    const payload = {
      body_text: editForm.body_text?.trim() || undefined, body_html: editForm.body_html?.trim() || undefined,
      type: editForm.type || undefined, contact_id: editForm.contact_id ?? undefined,
      recipients: Array.isArray(editForm.recipients) ? editForm.recipients : undefined,
      attachments: Array.isArray(editForm.attachments) ? editForm.attachments : undefined,
    };
    if (editForm.occurred_at) payload.occurred_at = dayjs(editForm.occurred_at).toISOString();
    return payload;
  }

  async function saveEdit() {
    if (!editingItem?.id) return;
    if (editForm.type === "file" && !(editForm.attachments || []).length) { setWarning("Add a PDF before saving a file activity."); return; }
    if (!editForm.subject || !editForm.subject.trim()) { setWarning("Subject is required."); return; }
    setEditSaving(true);
    try {
      const res = editForm.type === "file"
        ? await api.patch(url(`/company-activity/${editingItem.id}/file`), buildFileActivityFormData(editForm))
        : await api.patch(url(`/company-activity/${editingItem.id}`), { ...buildEditPayload(), subject: editForm.subject.trim() });
      setItems((prev) => prev.map((x) => (x.id === res.data.id ? res.data : x)));
      setEditOpen(false);
      setEditingItem(null);
      fetchList({ silent: true });
    } catch (err) {
      setWarning(formatErrorDetail(err?.response?.data?.detail, "Failed to update activity."));
    } finally {
      setEditSaving(false);
    }
  }

  async function addEditAttachment(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const attachment = await buildPdfAttachment(file);
      setEditForm((prev) => ({
        ...prev,
        subject: prev.subject?.trim() ? prev.subject : getAttachmentName(attachment),
        attachments: [attachment],
      }));
      setWarning("");
    } catch (err) {
      setWarning(err?.message || "Failed to add PDF.");
    }
  }

  function removeEditAttachment(index) {
    setEditForm((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, currentIndex) => currentIndex !== index),
    }));
  }

  function confirmDelete(item) { setDeleteTarget(item); setDeleteOpen(true); }

  async function performDelete() {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      if (deleteTarget.source_kind === "company_email") {
        await api.delete(url(`/company-emails/${deleteTarget.raw_id}`));
      } else {
        await api.delete(url(`/company-activity/${deleteTarget.id}`));
      }
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setDeleteOpen(false);
      setDeleteTarget(null);
      fetchList({ silent: true });
    } catch (err) {
      setWarning(formatErrorDetail(err?.response?.data?.detail, "Failed to delete activity."));
    } finally {
      setDeleting(false);
    }
  }

  const totalPages = serverTotal != null
    ? Math.max(1, Math.ceil(serverTotal / pageSize))
    : items.length < pageSize ? page : page + 1;
  const canAddPdfAttachments = Boolean(companyId || prospectId || projectId);
  const showCreatePdfField = canAddPdfAttachments && form.type === "file";
  const showEditPdfField = canAddPdfAttachments && editForm.type === "file";

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "rgba(0,51,108,0.07)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TimelineIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND_BLUE }}>
            Activity Timeline
          </Typography>
          {serverTotal != null && (
            <Chip label={serverTotal} size="small" sx={{ bgcolor: "rgba(0,51,108,0.07)", color: BRAND_BLUE, fontWeight: 700, fontSize: 12 }} />
          )}
        </Box>
        <Button variant="contained" onClick={() => setOpenCreate(true)} sx={SECONDARY_BUTTON_SX}>
          + Add Activity
        </Button>
      </Box>

      {warning && <Alert severity="warning" sx={{ mb: 2 }}>{warning}</Alert>}

      {/* Search + type filter chips */}
      <Box sx={{ mb: 2.5 }}>
        <TextField
          fullWidth
          size="small"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Search activities…"
          sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { bgcolor: "white", borderRadius: 2 } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "text.disabled", fontSize: 20 }} /></InputAdornment>,
            endAdornment: q ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setQ("")}><ClearIcon fontSize="small" /></IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {TYPE_FILTERS.map(({ value, label }) => {
            const cfg = value ? TYPE_CONFIG[value] : null;
            const isActive = typeFilter === value;
            return (
              <Chip
                key={value}
                label={label}
                size="small"
                onClick={() => { setTypeFilter(value); setPage(1); }}
                sx={{
                  cursor: "pointer",
                  fontWeight: isActive ? 700 : 500,
                  fontSize: 12,
                  bgcolor: isActive ? (cfg ? cfg.bg : BRAND_BLUE) : "white",
                  color: isActive ? (cfg ? cfg.color : "white") : "text.secondary",
                  border: `1.5px solid ${isActive ? (cfg ? cfg.color : BRAND_BLUE) : "rgba(0,0,0,0.12)"}`,
                  transition: "all 0.15s ease",
                  "&:hover": cfg
                    ? { bgcolor: cfg.bg, color: cfg.color, border: `1.5px solid ${cfg.color}` }
                    : { bgcolor: "rgba(0,51,108,0.07)" },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Timeline list */}
      <Box sx={{ maxHeight: 520, overflowY: "auto", pr: 0.5 }}>
        {loading ? (
          <BrandSpinner compact py={6} />
        ) : items.length === 0 ? (
          <Box sx={{ py: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "rgba(0,51,108,0.06)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TimelineIcon sx={{ color: "text.disabled", fontSize: 24 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>No activity yet</Typography>
            <Typography variant="caption" color="text.disabled">Add an activity to start tracking interactions</Typography>
          </Box>
        ) : (
          <Box>
            {items.map((it, idx) => (
              <ActivityRow
                key={it.id}
                item={it}
                isLast={idx === items.length - 1}
                apiBase={apiBase}
                onOpenEmail={openEmail}
                onEdit={openEdit}
                onDelete={confirmDelete}
                onView={(item) => { setViewItem(item); setViewOpen(true); }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Pagination
            page={page}
            onChange={(_, p) => setPage(p)}
            count={totalPages}
            size="small"
            sx={{
              "& .MuiPaginationItem-root": { color: BRAND_BLUE, borderColor: BRAND_BLUE },
              "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: BRAND_BLUE, color: "#fff" },
              "& .MuiPaginationItem-root:hover": { backgroundColor: "rgba(0,51,108,0.08)" },
            }}
          />
        </Stack>
      )}

      {/* Create dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm" PaperProps={BRAND_MODAL_PAPER_PROPS}>
        <DialogTitle sx={BRAND_MODAL_TITLE_SX}>Add Activity</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField
                select size="small" value={form.type}
                SelectProps={{ displayEmpty: true, renderValue: (v) => v || "Type" }}
                onChange={(e) => updateCreateType(e.target.value)}
                sx={{
                  flex: 1,
                  "& .MuiSelect-select": { color: BRAND_BLUE },
                }}
              >
                <MenuItem value="note" sx={{ color: BRAND_BLUE }}>Note</MenuItem>
                <MenuItem value="email" sx={{ color: BRAND_BLUE }}>Email</MenuItem>
                <MenuItem value="call" sx={{ color: BRAND_BLUE }}>Call</MenuItem>
                <MenuItem value="meeting" sx={{ color: BRAND_BLUE }}>Meeting</MenuItem>
                <MenuItem value="task" sx={{ color: BRAND_BLUE }}>Task</MenuItem>
                <MenuItem value="status" sx={{ color: BRAND_BLUE }}>Status</MenuItem>
                {canAddPdfAttachments && <MenuItem value="file" sx={{ color: BRAND_BLUE }}>File</MenuItem>}
              </TextField>
              <TextField
                type="date" size="small" label="Date" value={form.occurred_at}
                onChange={(e) => setForm({ ...form, occurred_at: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField fullWidth required size="small" placeholder="Subject" value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              error={!form.subject?.trim()} helperText={!form.subject?.trim() ? "Subject is required" : ""}
            />
            {form.type !== "file" && (
              <TextField placeholder={form.type === "email" ? "Email body" : "Notes"} multiline minRows={6}
                value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })}
              />
            )}
            {showCreatePdfField && (
              <ActivityAttachmentField
                attachments={form.attachments || []}
                onAdd={addCreateAttachment}
                onRemove={removeCreateAttachment}
                inputRef={createFileInputRef}
                disabled={creating}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCreate} variant="contained" disabled={creating}
            sx={{ backgroundColor: BRAND_GREEN, color: "#fff", "&:hover": { backgroundColor: "#507011" }, "&.Mui-disabled": { backgroundColor: "rgba(100,131,21,0.4)", color: "#fff" } }}
          >
            {creating ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm" PaperProps={BRAND_MODAL_PAPER_PROPS}>
        <DialogTitle sx={BRAND_MODAL_TITLE_SX}>Edit Activity</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} mt={1}>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField select size="small" value={editForm.type}
                SelectProps={{ displayEmpty: true, renderValue: (v) => v || "Type" }}
                onChange={(e) => updateEditType(e.target.value)}
                sx={{
                  flex: 1,
                  "& .MuiSelect-select": { color: BRAND_BLUE },
                }}
              >
                <MenuItem value="note" sx={{ color: BRAND_BLUE }}>Note</MenuItem>
                <MenuItem value="email" sx={{ color: BRAND_BLUE }}>Email</MenuItem>
                <MenuItem value="call" sx={{ color: BRAND_BLUE }}>Call</MenuItem>
                <MenuItem value="meeting" sx={{ color: BRAND_BLUE }}>Meeting</MenuItem>
                <MenuItem value="task" sx={{ color: BRAND_BLUE }}>Task</MenuItem>
                <MenuItem value="status" sx={{ color: BRAND_BLUE }}>Status</MenuItem>
                {canAddPdfAttachments && <MenuItem value="file" sx={{ color: BRAND_BLUE }}>File</MenuItem>}
              </TextField>
              <TextField
                type="date" size="small" label="Date"
                value={editForm.occurred_at ? dayjs(editForm.occurred_at).format("YYYY-MM-DD") : ""}
                onChange={(e) => setEditForm({ ...editForm, occurred_at: e.target.value ? dayjs(e.target.value).toDate() : null })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField fullWidth required size="small" placeholder="Subject" value={editForm.subject}
              onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
              error={!editForm.subject?.trim()} helperText={!editForm.subject?.trim() ? "Subject is required" : ""}
            />
            {editForm.type !== "file" && (
              <TextField placeholder={editForm.type === "email" ? "Email body" : "Notes"} multiline minRows={6}
                value={editForm.body_text} onChange={(e) => setEditForm({ ...editForm, body_text: e.target.value })}
              />
            )}
            {showEditPdfField && (
              <ActivityAttachmentField
                attachments={editForm.attachments || []}
                onAdd={addEditAttachment}
                onRemove={removeEditAttachment}
                inputRef={editFileInputRef}
                disabled={editSaving}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={saveEdit} variant="contained" disabled={editSaving}
            sx={{ backgroundColor: BRAND_GREEN, color: "#fff", "&:hover": { backgroundColor: "#507011" }, "&.Mui-disabled": { backgroundColor: "rgba(100,131,21,0.4)", color: "#fff" } }}
          >
            {editSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        onConfirm={performDelete}
        loading={deleting}
        title="Delete Activity"
        description="Are you sure you want to delete this activity? This action can't be undone."
      />

      {/* View dialog */}
      <Dialog open={viewOpen} onClose={() => { setViewOpen(false); setViewItem(null); }} fullWidth maxWidth="sm" PaperProps={BRAND_MODAL_PAPER_PROPS}>
        <DialogTitle sx={BRAND_MODAL_TITLE_SX}>
          {viewItem?.subject || (viewItem?.type ? viewItem.type.toUpperCase() : "Activity")}
        </DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            <Typography variant="caption" color="text.secondary">
              {viewItem?.type?.toUpperCase()} • {viewItem?.occurred_at ? dayjs(viewItem.occurred_at).format("MMM D, YYYY h:mm A") : ""}
            </Typography>
            {viewItem?.body_html ? (
              <Box sx={{ typography: "body1", color: BRAND_BLUE, "& p": { mb: 1 } }} dangerouslySetInnerHTML={{ __html: viewItem.body_html }} />
            ) : (
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", color: BRAND_BLUE }}>
                {viewItem?.body_text || "No details available."}
              </Typography>
            )}
            {Array.isArray(viewItem?.attachments) && viewItem.attachments.length > 0 && (
              <Stack spacing={1}>
                {viewItem.attachments.map((attachment, index) => (
                  <Box
                    key={`${getAttachmentName(attachment)}-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      border: "1px solid rgba(0,51,108,0.12)",
                      borderRadius: 1,
                      px: 1.25,
                      py: 0.75,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: BRAND_BLUE, fontWeight: 600 }} noWrap>
                      {getAttachmentName(attachment)}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                      <Tooltip title="Open PDF">
                        <IconButton
                          size="small"
                          onClick={() => openSavedAttachment({
                            activityId: viewItem.id,
                            attachmentIndex: index,
                            attachment,
                            apiBase,
                          })}
                        >
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download PDF">
                        <IconButton
                          size="small"
                          onClick={() => downloadSavedAttachment({
                            activityId: viewItem.id,
                            attachmentIndex: index,
                            attachment,
                            apiBase,
                          })}
                        >
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setViewOpen(false); setViewItem(null); }} sx={{ color: BRAND_BLUE }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Email viewer dialog */}
      <Dialog open={viewerOpen} onClose={() => { setViewerOpen(false); setViewerMessageId(null); setViewerAutoLog(true); }}
        fullWidth maxWidth="md" PaperProps={{ sx: { ...BRAND_MODAL_PAPER_PROPS.sx, height: "80vh" } }}
      >
        <DialogTitle sx={BRAND_MODAL_TITLE_SX}>Email</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {viewerMessageId ? (
            <Box sx={{ height: "100%", p: 2 }}>
              <MessageViewer messageId={viewerMessageId} autoLog={viewerAutoLog} onLogged={onViewerLogged} companyId={companyId} prospectId={prospectId} />
            </Box>
          ) : (
            <BrandSpinner compact py={4} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ActivityRow({ item, isLast, apiBase, onOpenEmail, onEdit, onDelete, onView }) {
  const cfg = TYPE_CONFIG[item?.type] || TYPE_CONFIG.status;
  const Icon = cfg.Icon;
  const isEmail = item?.type === "email";
  const when = formatRelativeDate(item?.occurred_at);
  const fullDate = item?.occurred_at ? dayjs(item.occurred_at).format("MMM D, YYYY h:mm A") : "";

  return (
    <Box sx={{ display: "flex", gap: 0, position: "relative" }}>
      {/* Timeline spine */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 44, flexShrink: 0, pt: 0.5 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: "50%",
          bgcolor: cfg.bg, border: `2px solid ${cfg.color}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: cfg.color, flexShrink: 0, zIndex: 1,
        }}>
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        {!isLast && (
          <Box sx={{ width: 2, flex: 1, minHeight: 20, bgcolor: "rgba(0,51,108,0.1)", mt: 0.5, mb: 0 }} />
        )}
      </Box>

      {/* Content card */}
      <Box
        sx={{
          flex: 1, mb: isLast ? 0 : 1.5, ml: 1.5,
          p: 1.5, borderRadius: 2, bgcolor: "white",
          border: "1px solid rgba(0,51,108,0.08)",
          cursor: "pointer",
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
          "&:hover": {
            borderColor: cfg.color,
            boxShadow: `0 2px 10px ${cfg.color}22`,
          },
        }}
        onClick={() => {
          if (isEmail) {
            const graphId = item.graph_message_id || item.external_id || item.internet_message_id;
            if (graphId) onOpenEmail?.(item);
          } else {
            onView?.(item);
          }
        }}
        role="button"
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Subject + type chip */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25, flexWrap: "wrap" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700, color: BRAND_BLUE, lineHeight: 1.3,
                  textDecoration: isEmail ? "underline" : "none",
                }}
              >
                {item.subject || (item.type ? item.type.toUpperCase() : "Activity")}
              </Typography>
              <Chip
                label={cfg.label}
                size="small"
                sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 10, height: 18, "& .MuiChip-label": { px: 0.75 } }}
              />
            </Box>

            {/* Body preview */}
            {item.body_text && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mb: 0.5 }}>
                {item.body_text}
              </Typography>
            )}

            {/* Sender / recipients */}
            {isEmail && item.sender && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                From: {item.sender}
              </Typography>
            )}
            {isEmail && Array.isArray(item.recipients) && item.recipients.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                To: {item.recipients.map((r) => r.email).filter(Boolean).join(", ")}
              </Typography>
            )}

            {/* Date */}
            <Tooltip title={fullDate} placement="bottom-start">
              <Typography variant="caption" sx={{ color: "text.disabled", fontSize: 11, cursor: "default" }}>
                {when}
              </Typography>
            </Tooltip>

            {Array.isArray(item.attachments) && item.attachments.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                {item.attachments.map((attachment, index) => (
                  <Chip
                    key={`${getAttachmentName(attachment)}-${index}`}
                    size="small"
                    icon={<AttachFileIcon sx={{ fontSize: "14px !important" }} />}
                    label={getAttachmentName(attachment)}
                    onClick={(event) => {
                      event.stopPropagation();
                      openSavedAttachment({
                        activityId: item.id,
                        attachmentIndex: index,
                        attachment,
                        apiBase,
                      });
                    }}
                    sx={{
                      maxWidth: 220,
                      fontSize: 10,
                      height: 22,
                      bgcolor: "rgba(0,51,108,0.06)",
                      color: BRAND_BLUE,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
            {!item.read_only && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit?.(item)} sx={{ color: "text.secondary", "&:hover": { color: BRAND_BLUE } }}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {(item.source_kind === "company_email" || !item.read_only) && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete?.(item)} sx={{ color: "text.secondary", "&:hover": { color: "#D32F2F" } }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {item.external_id && (
              <Tooltip title={item.external_link ? "Open source" : "No link available"}>
                <span onClick={(e) => { e.stopPropagation(); if (item.external_link) window.open(item.external_link, "_blank"); }}>
                  <IconButton size="small" disabled={!item.external_link} sx={{ color: "text.secondary" }}>
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
